import React, { useState, useMemo, useContext } from 'react'
import PropTypes from 'prop-types'
import Offcanvas from 'react-bootstrap/Offcanvas'
import Spinner from 'react-bootstrap/Spinner'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { useQuery, useQueryClient } from 'react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { audience } from 'api/helpers'
import { apiGet } from 'api'
import { useTransaction } from 'api/hooks/use-transactions'
import {
  usePinnedFields,
  useCreatePinnedField,
  useDeletePinnedField
} from 'api/hooks/use-dashboard-pinned-fields'
import FlagCount from 'shared/flag-count'
import DrawerHeader from './header'
import DrawerDetails from './details'
import PinnedFieldsCard from './pinned-fields-card'
import Workflow from './workflow'
import EmployeeConflictTable from './employee-conflict-table'
import AnalyticsTable from 'transactions/drawer/analytics'
import ClearTransaction from './clear-transaction'
import RiskBadge from 'shared/badges/risk-badge'
import MonitoringReviewActions from 'monitoring/review-actions'
import AppBadge from 'shared/badges/app-badge'
import OverModalContext from 'context/over-modal-context'

function QuickLookDrawer({
  transactions,
  transactionId,
  show,
  setShow,
  dashboardId,
  canEdit = true,
  auditReportId,
  initialFeatureTab = null,
  monitoringReview,
  monitoringContext = null
}) {
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const [currentTransaction, setCurrentTransaction] = useState(transactionId)
  const [localReview, setLocalReview] = useState(monitoringReview)
  const overModal = useContext(OverModalContext)

  // Sync from props when the parent opens a different transaction
  React.useEffect(() => {
    setCurrentTransaction(transactionId)
  }, [transactionId])

  React.useEffect(() => {
    setLocalReview(monitoringReview)
  }, [transactionId, monitoringReview])

  const handleClose = () => {
    queryClient.invalidateQueries(['transactions'])
    queryClient.invalidateQueries(`transactions/${currentTransaction}`)
    if (auditReportId) {
      queryClient.invalidateQueries([`reports/${auditReportId}`])
      queryClient.invalidateQueries(
        `audit_reports/${auditReportId}/transactions/${currentTransaction}`
      )
    }
    setShow(false)
  }

  const { isSuccess, data: transaction } = useTransaction({
    id: currentTransaction
  })

  const { isFetching: auditIsFetching, data: auditData } = useQuery(
    `audit_reports/${auditReportId}/transactions/${currentTransaction}`,
    async () => {
      const url = `audit_reports/${auditReportId}/transactions/${currentTransaction}`
      const token = await getAccessTokenSilently({ audience })
      const data = await apiGet({ url, token })
      return data
    },
    { enabled: !!auditReportId && show, keepPreviousData: true }
  )

  const testingData = auditReportId
    ? {
        transaction_review: auditData?.transaction_review,
        features: auditData?.features
      }
    : undefined

  const reportAnalytics = auditReportId ? auditData?.report_analytics : undefined

  // Fetch pinned fields for this dashboard
  const { data: pinnedFieldsData, isSuccess: pinnedFieldsSuccess } =
    usePinnedFields({
      dashboardId,
      options: { enabled: !!dashboardId }
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pinnedFields = pinnedFieldsSuccess ? pinnedFieldsData : []

  // Create a Set of pinned identifiers for quick lookup
  // For TableColumns: use database_column_name
  // For CustomColumns: use custom_column_id
  const pinnedFieldIds = useMemo(() => {
    const ids = new Set()
    pinnedFields.forEach((pf) => {
      if (pf.pinnable_type === 'TableColumn') {
        ids.add(pf.pinnable.database_column_name)
      } else if (pf.pinnable_type === 'CustomColumn') {
        ids.add(`custom_${pf.pinnable.id}`)
      }
    })
    return ids
  }, [pinnedFields])

  // Mutations for pin/unpin
  const createPinnedFieldMutation = useCreatePinnedField({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(['dashboard-pinned-fields', dashboardId])
      }
    }
  })

  const deletePinnedFieldMutation = useDeletePinnedField({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(['dashboard-pinned-fields', dashboardId])
      }
    }
  })

  // Find table column ID by database column name from transaction details
  const findTableColumnId = (databaseColumnName) => {
    if (!transaction?.grouped_transaction_details) return null

    for (const [, fields] of Object.entries(
      transaction.grouped_transaction_details
    )) {
      const field = fields.find(
        (f) => f.database_column_name === databaseColumnName
      )
      if (field && field.table_column_id) {
        return field.table_column_id
      }
    }
    return null
  }

  // Handler for pinning/unpinning fields (for both TableColumn and CustomColumn)
  const handlePinField = (field, isPinned, fieldType = 'TableColumn') => {
    if (!dashboardId) return

    if (isPinned) {
      // Unpin: find the pinned field entry and delete it
      let pinnedField
      if (fieldType === 'TableColumn') {
        pinnedField = pinnedFields.find(
          (pf) =>
            pf.pinnable_type === 'TableColumn' &&
            pf.pinnable.database_column_name === field.database_column_name
        )
      } else if (fieldType === 'CustomColumn') {
        pinnedField = pinnedFields.find(
          (pf) =>
            pf.pinnable_type === 'CustomColumn' && pf.pinnable.id === field.id
        )
      }

      if (pinnedField) {
        deletePinnedFieldMutation.mutate({
          dashboardId,
          pinnedFieldId: pinnedField.id
        })
      }
    } else {
      // Pin: create a new pinned field
      let pinnableId

      if (fieldType === 'TableColumn') {
        pinnableId =
          field.table_column_id || findTableColumnId(field.database_column_name)
      } else if (fieldType === 'CustomColumn') {
        pinnableId = field.id
      }

      if (pinnableId) {
        createPinnedFieldMutation.mutate({
          dashboard_id: dashboardId,
          pinnable_id: pinnableId,
          pinnable_type: fieldType
        })
      }
    }
  }

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      scroll={true}
      backdrop={true}
      // Level with the modal rather than above it: later in the DOM, the drawer still covers the
      // modal, and a modal the drawer opens covers the drawer.
      style={{ width: '60%', ...(overModal && { zIndex: 1055 }) }}
      backdropClassName={overModal ? 'over-modal' : undefined}
    >
      {isSuccess && (
        <DrawerHeader
          transactions={transactions}
          currentTransaction={currentTransaction}
          setCurrentTransaction={setCurrentTransaction}
          transaction={transaction}
          handleClose={handleClose}
        />
      )}

      <Offcanvas.Body>
        {isSuccess && localReview !== undefined && (
          <div
            className="mb-3 p-3 rounded"
            style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted text-uppercase fw-bold" style={{ fontSize: '14px' }}>
                Monitoring review
              </div>
              {localReview?.status === 'Resolved' && (
                <AppBadge variant="secondary" className="fw-normal" style={{ fontSize: '14px', padding: '6px 12px' }}>
                  Resolved{localReview?.reason ? ` · ${localReview.reason}` : ''}
                </AppBadge>
              )}
              {localReview?.status === 'Ignored' && (
                <AppBadge variant="secondary" className="fw-normal" style={{ fontSize: '14px', padding: '6px 12px' }}>
                  Ignored{localReview?.reason ? ` · ${localReview.reason}` : ''}
                </AppBadge>
              )}
            </div>
            <MonitoringReviewActions
              transactionId={currentTransaction}
              review={localReview}
              onChange={(newReview) => setLocalReview(newReview)}
              sectionId={monitoringContext?.sectionId}
              sectionAbbreviation={monitoringContext?.sectionAbbreviation}
              vendorId={monitoringContext?.vendorId}
              vendorName={monitoringContext?.vendorName}
              employeeId={monitoringContext?.employeeId}
              employeeName={monitoringContext?.employeeName}
              controlAnalytics={monitoringContext?.controlAnalytics || []}
              highRisk={monitoringContext?.highRisk || false}
            />
          </div>
        )}

        {isSuccess && false && (
          <div className="mt-2 mb-4">
            <ClearTransaction
              transaction={transaction}
              dashboardId={dashboardId}
            />
          </div>
        )}

        {isSuccess && dashboardId && (
          <PinnedFieldsCard
            pinnedFields={pinnedFields}
            transaction={transaction}
            dashboardId={dashboardId}
            canEdit={canEdit}
          />
        )}

        {isSuccess && dashboardId && <hr />}

        {auditReportId && auditIsFetching && (
          <Row>
            <Col sm="auto"></Col>
            <Col sm="auto">
              <div className="my-2 py-2">
                <Spinner animation="grow" variant="warning" size="sm" />
              </div>
            </Col>
            <Col sm="auto"></Col>
          </Row>
        )}

        {isSuccess && (
          <DrawerDetails
            transaction={transaction}
            dashboardId={dashboardId}
            onPinField={dashboardId && canEdit ? handlePinField : undefined}
            pinnedFieldIds={dashboardId ? pinnedFieldIds : undefined}
            canEdit={canEdit}
            testingData={testingData}
            auditReportId={auditReportId}
            initialFeatureTab={initialFeatureTab}
          />
        )}

        <hr />

        <h5>
          Risk Score{' '}
          {isSuccess && (
            <RiskBadge
              count={parseFloat(transaction.risk_score)}
              showCount={true}
            />
          )}
          <span className="ms-4"></span>
          Analytics Flagged <span className="ms-2"></span>
          {isSuccess && <FlagCount count={transaction.analytics_count} />}
        </h5>
        {isSuccess && <AnalyticsTable analytics={transaction.analytics} reportAnalytics={reportAnalytics} />}
        {isSuccess && transaction.employee && false && (
          <EmployeeConflictTable transaction={transaction} />
        )}
        {isSuccess && (
          <div className="mt-4">
            <Workflow transactionId={transaction.id} />
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}

QuickLookDrawer.propTypes = {
  transactions: PropTypes.array.isRequired,
  transactionId: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  dashboardId: PropTypes.string,
  canEdit: PropTypes.bool,
  auditReportId: PropTypes.string,
  monitoringReview: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    reason: PropTypes.string
  }),
  monitoringContext: PropTypes.shape({
    sectionId: PropTypes.string,
    sectionAbbreviation: PropTypes.string,
    vendorId: PropTypes.string,
    vendorName: PropTypes.string,
    employeeId: PropTypes.string,
    employeeName: PropTypes.string,
    controlAnalytics: PropTypes.array,
    highRisk: PropTypes.bool
  })
}

export default QuickLookDrawer
